import React, { useEffect, useState } from 'react'
import { Box, Grid, Icon, Typography } from '@mui/material'
import InterviewDatePicker from '../InterviewDatePicker'
import TimeSelector from './TimeSelector'
import SchedulerGrid from './SchedulerGrid'
import ParticipantPanel from '../ParticipantPanel'
import InterviewDetailsForm from '../InterviewDetailsForm'
import { getEventList } from 'services/api/modules/recruitment/scheduler'
import { useQuery, useQueryClient } from 'react-query'
import { format } from 'date-fns'
import { timestampToDecimal } from 'services/helper/timestampFormatter'

const Scheduler = ({
  candidateId,
  candidateName,
  candidateEmail,
  candidateImage,
  interviewData,
  candidate_id_for_edit_email,
  isEdit,
  profile_type,
}) => {
  const queryClient = useQueryClient()
  const [isFirstPage, setIsFirstPage] = useState(true)
  const [currentDate, setCurrentDate] = useState(
    isEdit ? new Date(interviewData.start_date) : new Date()
  )
  const [currentWeekStartDate, setCurrentWeekStartDate] = useState(getStartOfWeek())
  const [selectedEvent, setSelectedEvent] = useState({
    date: isEdit ? new Date(interviewData.start_date) : null,
    start: isEdit ? timestampToDecimal(interviewData.start_time) : null,
    end: isEdit ? timestampToDecimal(interviewData.end_time) : null,
  })
  const [interviewerList, setInterviewerList] = useState(
    isEdit ? [...interviewData.relations.attendee] : []
  )

  const currentYear = Number(format(currentDate, 'yyyy'))
  const currentMonth = Number(format(currentDate, 'MM'))

  const { data: eventData, isLoading } = useQuery(
    ['getEventList', currentYear, currentMonth],
    async () => {
      const { data } = await getEventList(currentYear, currentMonth)
      return data
    },
    {
      refetchOnWindowFocus: false,
      enabled: currentYear !== undefined && currentMonth !== undefined,
    }
  )
  const events =
    !isLoading && isEdit
      ? eventData
          .filter((e) => e.id !== interviewData.id)
          .map((event) => ({
            title: event.title,
            date: new Date(event.start_date),
            start: timestampToDecimal(event.start_time),
            end: event.end_time === '00:00:00' ? 25 : timestampToDecimal(event.end_time),
            candidate_name: event.relations.candidateable.relations.candidateable.name,
            candidate_image_url:
              event.relations.candidateable.relations.candidateable.profile_image_url,
          }))
      : !isLoading && !isEdit
      ? eventData.map((event) => ({
          title: event.title,
          date: new Date(event.start_date),
          start: timestampToDecimal(event.start_time),
          end: event.end_time === '00:00:00' ? 25 : timestampToDecimal(event.end_time),
          candidate_name: event.relations.candidateable.relations.candidateable.name,
          candidate_image_url:
            event.relations.candidateable.relations.candidateable.profile_image_url,
        }))
      : []

  function getStartOfWeek() {
    const dayOfWeek = currentDate.getDay()
    const startOfWeek = new Date(currentDate)
    startOfWeek.setDate(currentDate.getDate() - dayOfWeek)
    return startOfWeek
  }

  const handlePrevWeekClick = () => {
    const prevWeekStartDate = new Date(currentWeekStartDate)
    prevWeekStartDate.setDate(currentWeekStartDate.getDate() - 7)
    setCurrentWeekStartDate(prevWeekStartDate)
    setCurrentDate(prevWeekStartDate)
  }

  const handleNextWeekClick = () => {
    const nextWeekStartDate = new Date(currentWeekStartDate)
    nextWeekStartDate.setDate(currentWeekStartDate.getDate() + 7)
    setCurrentWeekStartDate(nextWeekStartDate)
    setCurrentDate(nextWeekStartDate)
  }

  const generateTimeSlots = () => {
    const timeSlots = []
    let hour = 0
    let minute = 0

    for (let i = 0; i <= 96; i++) {
      const period = hour > 11 && hour < 24 ? 'PM' : 'AM'
      const time = (hour % 12 || 12) + ':' + (minute === 0 ? '00' : minute) + ' ' + period
      const value = hour + minute / 60 + 1

      timeSlots.push({ time, value })

      minute += 15
      if (minute === 60) {
        minute = 0
        hour++
      }
    }

    return timeSlots
  }

  useEffect(() => {
    // Whenever currentYear or currentMonth changes, invalidate the query to trigger a re-fetch.
    if (currentYear !== undefined && currentMonth !== undefined) {
      queryClient.invalidateQueries(['getEventList', currentYear, currentMonth])
    }
  }, [currentYear, currentMonth])

  return (
    <Grid container sx={{ backgroundColor: 'default-background.main' }}>
      {isFirstPage ? (
        <React.Fragment>
          <Grid item xs={4} sx={{ backgroundColor: 'white.main' }}>
            <ParticipantPanel
              candidateName={candidateName}
              candidateEmail={candidateEmail}
              candidateImage={candidateImage}
              profile_type={profile_type}
              interviewId={
                interviewData?.relations?.jobAdCandidate?.relations?.candidateable?.id ||
                candidate_id_for_edit_email
              }
              interviewerList={interviewerList}
              setInterviewerList={setInterviewerList}
            />
          </Grid>
          <Grid item xs={8} sx={{ backgroundColor: 'white.main' }}>
            <Box sx={{ height: 'calc(100vh - 85px)', overflowY: 'auto', position: 'relative' }}>
              {/* Calendar */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '16px 24px',
                  backgroundColor: 'white.main',
                  zIndex: 2,
                  position: 'sticky',
                  top: 0,
                  borderBottom: '1px solid #EDEFF0',
                }}
              >
                <Typography>Date & Time</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <Icon
                    sx={{
                      cursor: 'pointer',
                      color: 'light-gray-text.main',
                      height: '35px',
                      width: '35px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '3.5px',
                      border: '1px solid #8A8A8A',
                    }}
                    onClick={handlePrevWeekClick}
                  >
                    chevron_left
                  </Icon>
                  <InterviewDatePicker
                    currentDate={currentDate}
                    setCurrentDate={setCurrentDate}
                    selectedEvent={selectedEvent}
                    setSelectedEvent={setSelectedEvent}
                    setCurrentWeekStartDate={setCurrentWeekStartDate}
                  />
                  <Icon
                    sx={{
                      cursor: 'pointer',
                      color: 'light-gray-text.main',
                      height: '35px',
                      width: '35px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '3.5px',
                      border: '1px solid #8A8A8A',
                    }}
                    onClick={handleNextWeekClick}
                  >
                    chevron_right
                  </Icon>
                </Box>
              </Box>

              {/* Scheduler */}
              <SchedulerGrid
                events={events}
                selectedEvent={selectedEvent}
                setSelectedEvent={setSelectedEvent}
                currentWeekStartDate={currentWeekStartDate}
                setCurrentDate={setCurrentDate}
                interviewData={interviewData}
                isEdit={isEdit}
              />

              {/* Bottom pane */}
              <TimeSelector
                selectedEvent={selectedEvent}
                setSelectedEvent={setSelectedEvent}
                setIsFirstPage={setIsFirstPage}
                timeSlotValue={generateTimeSlots()}
              />
            </Box>
          </Grid>
        </React.Fragment>
      ) : (
        <Grid item xs={8} sx={{ marginX: 'auto' }}>
          <InterviewDetailsForm
            candidateId={candidateId}
            candidateName={candidateName}
            candidateEmail={candidateEmail}
            candidateImage={candidateImage}
            interviewerList={interviewerList}
            setIsFirstPage={setIsFirstPage}
            selectedEvent={selectedEvent}
            timeSlotValue={generateTimeSlots()}
            interviewData={interviewData}
            isEdit={isEdit}
          />
        </Grid>
      )}
    </Grid>
  )
}

export default Scheduler
